import { Button } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { WorkflowStatusPopover } from 'components/workflow-status-popover/workflow-status-popover';
import { useIcWorkspaceProgress } from './use-ic-workspace-progress';
import type { CreateWorkspaceResponse } from './types';
import style from './workspace-creation-progress.module.scss';

interface WorkspaceCreationProgressProps {
  creation: CreateWorkspaceResponse;
  onDismiss: () => void;
}

// The "main place" status chip shown after a workspace is created: a spinner
// (clock) that turns into a green check when the background creation workflow
// finishes. Clicking the icon opens the per-step checklist popover. On success
// it navigates the user straight to the now-usable workspace (same tab).
export const WorkspaceCreationProgress = ({
  creation,
  onDismiss,
}: WorkspaceCreationProgressProps) => {
  const { statusItems, overallStatus, isComplete, isFailed } =
    useIcWorkspaceProgress({
      channel: creation.resources.notifications.channel,
      events: creation.resources.notifications.events,
      processStatusHref: creation.queries.processStatus.href,
    });
  const history = useHistory();

  // On success, take the user straight to the now-usable workspace (same tab).
  // Fires only on 'processed' (isComplete) — never while building or on failure.
  useEffect(() => {
    if (isComplete) history.push(creation.href);
  }, [isComplete, creation.href, history]);

  const label = isFailed
    ? `Couldn't create "${creation.name}"`
    : isComplete
      ? `Opening "${creation.name}"…`
      : `Creating "${creation.name}"…`;

  return (
    <div className={style.container} data-testid="workspace-creation-progress">
      <WorkflowStatusPopover
        statusItems={statusItems}
        overallStatus={overallStatus}
        iconDescription="Workspace creation status"
        ariaLabel="Workspace creation status"
        dataTestId="workspace-creation-status"
      />
      <span className={style.label}>{label}</span>
      <Button
        kind="ghost"
        size="sm"
        hasIconOnly
        renderIcon={Close}
        iconDescription="Dismiss"
        tooltipPosition="left"
        onClick={onDismiss}
      />
    </div>
  );
};
